import { useCallback } from "react"

import { useApiLoggingDemo } from "@/api/useDemo"
import * as TriageAPI from "@/api/useTriage"
import { App } from "@/atoms"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import { predicateHelpers } from "@/hooks/useHttp"
import useServices from "@/hooks/useServices"
import { useFeatureFlags } from "@/hooks/useUser"
import { overrideDemoIssueResponse } from "@/organisms/Drawers/components/TriageDrawer/components/DemoOverrides/demoOverrides"
import { enrichDemoIssuesResponse } from "@/organisms/Drawers/components/TriageDrawer/components/MitigationDemoCards/enrich"
import { getIssuesTriageSelector } from "@/pages/TriagePage/application/useTriage.selector"
import {
  getAttackChains,
  getFindingsInfoForExpandRow
} from "./useTriage.selectors"

import type { IssuesTriageResponse } from "@/api/useTriage.types"
import type { Issue } from "@/domain/issue"
import type { Params, QueryClient } from "@/hooks/useHttp.types"

// Poll the issue while any finding is still being analyzed so the "Analyzing
// Data Flow" state resolves on its own once dataflow completes upstream.
const IN_PROGRESS_REFETCH_MS = 15000

// Passed directly as React Query's refetchInterval; it reads the cached
// (pre-select) response off the query — typed on just the field we touch so it
// stays assignable without restating React Query's full generic signature.
const refetchWhileAnalyzing = (query: {
  state: { data?: IssuesTriageResponse }
}) => {
  const issues = [query.state.data?.data].flat().filter(Boolean)
  // Match the card's `findingKai.inProgress ?? finding.inProgress` precedence:
  // poll while the finding OR any of its per-coordinate Kai entries is analyzing,
  // so a coordinate-level flag can't leave the UI "analyzing" without refetching.
  const analyzing = issues.some((issue) =>
    (issue?.findings ?? []).some(
      (finding) =>
        finding?.inProgress ||
        (finding?.kaiAnalysis?.sastLlmFindings?.findings ?? []).some(
          (coord) => coord?.inProgress
        )
    )
  )
  return analyzing ? IN_PROGRESS_REFETCH_MS : false
}

export const useIssueFindingsDrawer = (issueId: string | undefined) => {
  const context = TriageAPI.useIssue<
    KosmosApi.Response<Issue>,
    ReturnType<typeof getFindingsInfoForExpandRow>
  >(issueId, undefined, {
    enabled: !!issueId,
    select: getFindingsInfoForExpandRow
  })
  return context
}

export const useIssue = (issueId: string | undefined) => {
  const isInternalTenant = !!useFeatureFlags("isInternalTenant")
  const isDemo = useServices().auth.isDemo()
  const select = useCallback(
    (response: Parameters<typeof getIssuesTriageSelector>[0]) =>
      getIssuesTriageSelector(
        overrideDemoIssueResponse(
          enrichDemoIssuesResponse(response, isInternalTenant),
          isDemo
        )
      ),
    [isInternalTenant, isDemo]
  )
  const context = TriageAPI.useIssue(issueId, undefined, {
    enabled: !!issueId,
    select,
    refetchInterval: refetchWhileAnalyzing
  })

  return useApiLoggingDemo(context)
}

export const useIssueAndProjectDescription = (issueId: string | undefined) => {
  const context = useIssue(issueId)
  const projectDescriptionContext = TriageAPI.useProjectDescriptionTriage({
    needle: context?.response?.data?.[0]?.projectId
  })
  const isLoading = context.isLoading || projectDescriptionContext.isLoading

  return [context, projectDescriptionContext, isLoading] as const
}

export const useAttackScenarios = (
  issueId: string | undefined,
  params?: Params
) => {
  const context = TriageAPI.useAttackChains<ReturnType<typeof getAttackChains>>(
    issueId,
    params,
    { select: getAttackChains }
  )

  return context
}

const linkTicketToIssueOptions = (queryClient: QueryClient) => ({
  onSuccess: async () => {
    const { PROJECTS, ISSUES, ISSUES_COUNT } = RESOURCES_NAMES.TRIAGE

    queryClient.invalidateQueries({
      predicate: predicateHelpers.includesKeysPredicate([PROJECTS])
    })

    return queryClient.invalidateQueries({
      predicate: predicateHelpers.includesKeysPredicate(
        [ISSUES],
        [ISSUES_COUNT]
      )
    })
  }
})

export const useLinkTicketToIssue = () => {
  const { mutateAsync: link, isPending: isLinking } =
    TriageAPI.useCreateLinkTicketToIssue(linkTicketToIssueOptions)
  const { message } = App.useApp()

  return { isLinking, link, message }
}

export const useUnlinkTicketFromIssue = () => {
  const { mutateAsync: unlink, isPending: isUnlinking } =
    TriageAPI.useDeleteLinkTicketToIssue(linkTicketToIssueOptions)
  const { message } = App.useApp()

  return { isUnlinking, unlink, message }
}
